import React from "react";
import { cn } from "@/lib/utils";
import type DealStage from "@/model/interface/DealStage";
import { DealStageCategory, stageCategoryOf } from "@/enum/DealStageCategory";

interface DealStagePickerProps {
  stages: DealStage[];
  currentStageId?: string;
  personIsLead: boolean;
  disabled?: boolean;
  onPick: (stage: DealStage) => void;
}

interface StageGroup {
  key: string;
  title: string;
  categories: DealStageCategory[];
  leadHint?: string;
  hintConverts?: boolean;
}

const STAGE_GROUPS: StageGroup[] = [
  { key: "lead", title: "Lead stages", categories: [DealStageCategory.LEAD], leadHint: "stays a lead" },
  {
    key: "pipeline",
    title: "Pipeline",
    categories: [DealStageCategory.PIPELINE],
    leadHint: "becomes a contact",
    hintConverts: true,
  },
  {
    key: "closed",
    title: "Closed",
    categories: [DealStageCategory.WON, DealStageCategory.LOST],
    leadHint: "Won makes a contact",
    hintConverts: true,
  },
];

const DealStagePicker: React.FC<DealStagePickerProps> = ({ stages, currentStageId, personIsLead, disabled, onPick }) => {
  const activeStages = stages.filter((stage) => stage.isActive);

  return (
    <div className="space-y-3">
      {STAGE_GROUPS.map((group) => {
        const groupStages = activeStages.filter((stage) => group.categories.includes(stageCategoryOf(stage)));
        if (groupStages.length === 0) {
          return null;
        }
        return (
          <div key={group.key} className="space-y-1.5">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{group.title}</span>
              {personIsLead && group.leadHint && (
                <span className={cn("text-xs", group.hintConverts ? "text-info" : "text-muted-foreground")}>
                  {group.leadHint}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {groupStages.map((stage) => {
                const selected = stage.id === currentStageId;
                return (
                  <button
                    key={stage.id}
                    type="button"
                    disabled={disabled || selected}
                    onClick={() => onPick(stage)}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors disabled:cursor-default",
                      selected ? "border-primary bg-primary/10 text-foreground" : "hover:bg-muted",
                    )}
                  >
                    <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: stage.color }} />
                    {stage.name}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DealStagePicker;
